import { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SET_LOCALE } from 'store/actions';
import { useTheme } from '@mui/material/styles';
import {
  Avatar,
  Box,
  ButtonBase,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  Typography
} from '@mui/material';
import { IconLanguage } from '@tabler/icons-react';
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';

export const LOCALES = [
  { code: 'zh', label: '中文', short: '中文' },
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'vi', label: 'Tiếng Việt', short: 'VI' }
];

export default function LanguageButton() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const locale = useSelector((state) => state.customization.locale) || 'zh';
  const borderRadius = useSelector((state) => state.customization.borderRadius);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const current = LOCALES.find((l) => l.code === locale) || LOCALES[0];

  const handleToggle = () => setOpen((prev) => !prev);

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const handleSelect = (code) => {
    dispatch({ type: SET_LOCALE, locale: code });
    localStorage.setItem('locale', code);
    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }
    prevOpen.current = open;
  }, [open]);

  return (
    <Box
      sx={{
        ml: 2,
        mr: 3,
        [theme.breakpoints.down('md')]: {
          mr: 2
        }
      }}
    >
      <ButtonBase
        ref={anchorRef}
        sx={{ borderRadius: '12px' }}
        aria-controls={open ? 'language-menu' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Avatar
          variant="rounded"
          sx={{
            ...theme.typography.commonAvatar,
            ...theme.typography.mediumAvatar,
            width: 'auto',
            minWidth: '34px',
            px: 1.25,
            gap: 0.5,
            transition: 'all .2s ease-in-out',
            borderColor: theme.typography.menuChip.background,
            backgroundColor: theme.typography.menuChip.background,
            color: theme.palette.text.primary,
            '&[aria-controls="menu-list-grow"],&:hover': {
              background: theme.palette.secondary.dark,
              color: theme.palette.secondary.light
            }
          }}
          color="inherit"
        >
          <IconLanguage stroke={1.5} size="1.15rem" />
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1 }}>
            {current.short}
          </Typography>
        </Avatar>
      </ButtonBase>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [{ name: 'offset', options: { offset: [0, 14] } }]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                  <List
                    id="language-menu"
                    component="nav"
                    sx={{
                      width: '100%',
                      minWidth: 160,
                      backgroundColor: theme.palette.background.paper,
                      borderRadius: '10px',
                      py: 0.5
                    }}
                  >
                    {LOCALES.map((item) => (
                      <ListItemButton
                        key={item.code}
                        selected={item.code === locale}
                        sx={{ borderRadius: `${borderRadius}px`, mx: 0.5 }}
                        onClick={() => handleSelect(item.code)}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: item.code === locale ? 700 : 500 }}>
                              {item.label}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
